import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUser, FiBriefcase, FiBell, FiLock, FiCalendar, FiClock, FiAward, FiCreditCard, FiGlobe } from 'react-icons/fi';
import { profileAPI, authFetch } from '../utils/api';
import { ensureValidTenantId } from '../utils/auth';
import Modal from '../components/Modals/Modal';
import LogoUpload from '../components/LogoUpload/LogoUpload';
import { useTrialStatus } from '../hooks/useTrialStatus';
import CustomToast from '../components/common/CustomToast';
import { useTranslation } from 'react-i18next';
import { INDIAN_STATES, getStateFromGSTIN } from '../utils/indianStates';

const SettingsPage = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('profile');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [langModal, setLangModal] = useState({ show: false, targetLang: null });
  const [profile, setProfile] = useState({ name: '', email: '', phone: '' });
  const [notifications, setNotifications] = useState({
    newInvoiceNotification: true,
    paymentReceivedNotification: true,
    paymentOverdueNotification: true,
  });
  const [businessInfo, setBusinessInfo] = useState({
    name: '',
    address: '',
    state: '',
    taxId: '',
    gstin: '',
    email: '',
    mobile: '',
    logo: '',
    bankName: '',
    bankAccountNumber: '',
    bankIfscCode: '',
    bankBranch: '',
    bankHolderName: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bizSaving, setBizSaving] = useState(false);


  const accountId = localStorage.getItem('tenantId');

  // Debug: Log the accountId to see what's being stored
  console.log('Account ID from localStorage:', accountId);
  console.log('Account ID type:', typeof accountId);

  useEffect(() => {
    ensureValidTenantId();
    async function fetchProfile() {
      try {
        const data = (await profileAPI.getProfile()) || {};
        setProfile({ name: data.name || '', email: data.email || '', phone: data.phone || '' });
        setNotifications({
          newInvoiceNotification: data.newInvoiceNotification ?? true,
          paymentReceivedNotification: data.paymentReceivedNotification ?? true,
          paymentOverdueNotification: data.paymentOverdueNotification ?? true,
        });
        // Fetch business info from backend using accountId with user fallback
        const accountId = localStorage.getItem('tenantId');
        let biz = {};
        try {
          biz = (await profileAPI.getBusinessInfo(accountId)) || {};
        } catch (e) {
          console.warn('Error fetching business info with accountId, trying direct user endpoint:', e);
          try {
            biz = (await profileAPI.getBusinessInfo()) || {};
          } catch (err2) {
            biz = {};
          }
        }
        if (!biz || Object.keys(biz).length === 0) {
          try {
            biz = (await profileAPI.getBusinessInfo()) || {};
          } catch (e) {}
        }
        console.log('Fetched business info:', biz);
        const businessInfoData = {
          name: biz.business_name || data.business_name || '',
          address: biz.business_address || data.business_address || '',
          state: biz.business_state || data.business_state || getStateFromGSTIN(biz.business_gstin || data.business_gstin)?.name || '',
          taxId: biz.business_tax_id || data.business_tax_id || '',
          gstin: biz.business_gstin || data.business_gstin || '',
          email: biz.business_email || data.business_email || '',
          mobile: biz.business_mobile || data.business_mobile || '',
          logo: biz.business_logo || '',
          bankName: biz.bank_name || '',
          bankAccountNumber: biz.bank_account_number || '',
          bankIfscCode: biz.bank_ifsc_code || '',
          bankBranch: biz.bank_branch || '',
          bankHolderName: biz.bank_holder_name || ''
        };
        console.log('Setting business info state:', businessInfoData);
        setBusinessInfo(businessInfoData);
      } catch (err) {
        setToast({ show: true, message: 'Failed to load profile', type: 'error' });
      } finally {
        setLoading(false);
      }
    }
    fetchProfile();
  }, []);

  const handleChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleBizChange = (e) => {
    const { name, value } = e.target;
    setBusinessInfo(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'gstin') {
        const clean = value.trim().toUpperCase();
        if (clean.length >= 2) {
          const match = getStateFromGSTIN(clean);
          if (match && (!prev.state || prev.state === '')) {
            updated.state = match.name;
          }
        }
      }
      return updated;
    });
  };

  const handleLogoUpload = async (file) => {
    console.log('handleLogoUpload called with file:', file);
    if (!file) {
      console.log('No file provided');
      return;
    }

    // Validate accountId
    if (!accountId) {
      console.error('No accountId provided');
      setToast({ show: true, message: 'No account ID found. Please refresh the page and try again.', type: 'error' });
      return;
    }

    console.log('File details:', {
      name: file.name,
      type: file.type,
      size: file.size
    });

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setToast({ show: true, message: 'Please select an image file', type: 'error' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setToast({ show: true, message: 'Logo file size must be less than 5MB', type: 'error' });
      return;
    }

    try {
      setBizSaving(true);
      console.log('Uploading logo to account:', accountId);
      const response = await profileAPI.uploadBusinessLogo(accountId, file);
      console.log('Upload response:', response);
      if (response && response.logoUrl) {
        const newLogoUrl = response.logoUrl;
        console.log('Setting new logo URL:', newLogoUrl);
        setBusinessInfo(prev => {
          const updated = { ...prev, logo: newLogoUrl };
          return updated;
        });
        setToast({ show: true, message: 'Logo uploaded successfully!', type: 'success' });
      } else {
        setToast({ show: true, message: 'Failed to upload logo', type: 'error' });
      }
    } catch (err) {
      console.error('Logo upload error:', err);
      setToast({ show: true, message: 'Failed to upload logo: ' + (err.message || 'Unknown error'), type: 'error' });
    } finally {
      setBizSaving(false);
    }
  };

  const handleLogoDelete = async () => {
    // Validate accountId
    if (!accountId) {
      console.error('No accountId provided');
      setToast({ show: true, message: 'No account ID found. Please refresh the page and try again.', type: 'error' });
      return;
    }

    try {
      setBizSaving(true);
      const response = await profileAPI.deleteBusinessLogo(accountId);
      if (response && response.success) {
        setBusinessInfo(prev => ({ ...prev, logo: '' }));
        setToast({ show: true, message: 'Logo deleted successfully!', type: 'success' });
      } else {
        setToast({ show: true, message: 'Failed to delete logo', type: 'error' });
      }
    } catch (err) {
      console.error('Logo delete error:', err);
      setToast({ show: true, message: 'Failed to delete logo: ' + (err.message || 'Unknown error'), type: 'error' });
    } finally {
      setBizSaving(false);
    }
  };

  const handleNotificationChange = (e) => {
    setNotifications({
      ...notifications,
      [e.target.name]: e.target.checked,
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await profileAPI.updateProfile({
        name: profile.name,
        phone: profile.phone,
        ...notifications,
      });
      setToast({ show: true, message: 'Profile updated successfully!', type: 'success' });
    } catch (err) {
      setToast({ show: true, message: 'Failed to update profile', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleBizSave = async (e) => {
    e.preventDefault();
    setBizSaving(true);
    try {
      const payload = {
        business_name: businessInfo.name,
        business_address: businessInfo.address,
        business_state: businessInfo.state,
        business_tax_id: businessInfo.taxId,
        business_gstin: businessInfo.gstin,
        business_email: businessInfo.email,
        business_mobile: businessInfo.mobile,
        bank_name: businessInfo.bankName,
        bank_account_number: businessInfo.bankAccountNumber,
        bank_ifsc_code: businessInfo.bankIfscCode,
        bank_branch: businessInfo.bankBranch,
        bank_holder_name: businessInfo.bankHolderName
      };
      const res = await authFetch('/accounts/business-info', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to update business info');
      const updated = await res.json();
      console.log('Business info saved successfully. Server response:', updated);
      
      const successMessage = activeTab === 'bank' ? 'Bank details updated successfully!' : 'Business info updated successfully!';
      setToast({ show: true, message: successMessage, type: 'success' });
      
      setBusinessInfo(prev => ({
        ...prev,
        name: updated.business_name ?? prev.name,
        address: updated.business_address ?? prev.address,
        state: updated.business_state ?? prev.state,
        taxId: updated.business_tax_id ?? prev.taxId,
        gstin: updated.business_gstin ?? prev.gstin,
        email: updated.business_email ?? prev.email,
        mobile: updated.business_mobile ?? prev.mobile,
        logo: updated.business_logo ?? prev.logo,
        bankName: updated.bank_name ?? prev.bankName,
        bankAccountNumber: updated.bank_account_number ?? prev.bankAccountNumber,
        bankIfscCode: updated.bank_ifsc_code ?? prev.bankIfscCode,
        bankBranch: updated.bank_branch ?? prev.bankBranch,
        bankHolderName: updated.bank_holder_name ?? prev.bankHolderName
      }));
    } catch (err) {
      console.error('Error saving business info:', err);
      setToast({ show: true, message: 'Failed to update details', type: 'error' });
    } finally {
      setBizSaving(false);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <main className="page-container settings-page-container">
      <div className="page-header">
        <h1>{t('settings.title', 'Settings & Profile')}</h1>
      </div>

      <div className="settings-layout">
        <SubscriptionCountdownCard />

        <div className="settings-content-wrapper">
          {/* Navigation Sidebar */}
          <div className="settings-sidebar">
            <button className={`settings-tab ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}>
              <FiUser className="tab-icon" /> {t('settings.profile.title', 'Personal Profile')}
            </button>
            <button className={`settings-tab ${activeTab === 'business' ? 'active' : ''}`} onClick={() => setActiveTab('business')}>
              <FiBriefcase className="tab-icon" /> {t('settings.business.title', 'Business Details')}
            </button>
            <button className={`settings-tab ${activeTab === 'bank' ? 'active' : ''}`} onClick={() => setActiveTab('bank')}>
              <FiCreditCard className="tab-icon" /> {t('settings.bank.title', 'Bank Details')}
            </button>
            <button className={`settings-tab ${activeTab === 'security' ? 'active' : ''}`} onClick={() => setActiveTab('security')}>
              <FiLock className="tab-icon" /> {t('settings.security.tabTitle', 'Security & Alerts')}
            </button>
            <button className={`settings-tab ${activeTab === 'language' ? 'active' : ''}`} onClick={() => setActiveTab('language')}>
              <FiGlobe className="tab-icon" /> {t('settings.tabs.language', 'Language')}
            </button>
          </div>

          {/* Tab Content */}
          <div className="settings-panel">
            {activeTab === 'profile' && (
              <div className="settings-section">
                <div className="settings-header">
                  <FiUser />
                  <h2>{t('settings.profile.title', 'Personal Profile')}</h2>
                </div>
                <form onSubmit={handleSave}>
                  <div className="form-group">
                    <label className="form-label">{t('settings.profile.fullName', 'Full Name')}</label>
                    <input type="text" className="form-input" name="name" value={profile.name || ''} onChange={handleChange} placeholder="Enter your name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('settings.profile.emailAddress', 'Email Address')}</label>
                    <input type="email" className="form-input" name="email" value={profile.email || ''} disabled />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('settings.profile.phoneNumber', 'Phone Number')}</label>
                    <input type="tel" className="form-input" name="phone" value={profile.phone || ''} onChange={handleChange} placeholder="Enter your phone number" />
                  </div>
                  <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? t('settings.saving', 'Saving...') : t('settings.profile.saveProfile', 'Save Profile')}</button>
                </form>
              </div>
            )}

            {activeTab === 'business' && (
              <div className="settings-section">
                <div className="settings-header">
                  <FiBriefcase />
                  <h2>{t('settings.business.information', 'Business Information')}</h2>
                </div>
                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label className="form-label">{t('settings.business.logo', 'Business Logo')}</label>
                  <LogoUpload
                    currentLogo={businessInfo.logo}
                    onUpload={handleLogoUpload}
                    onDelete={handleLogoDelete}
                    loading={bizSaving}
                    disabled={bizSaving}
                    size={80}
                    showPreview={true}
                  />
                </div>
                <form onSubmit={handleBizSave}>
                  <div className="form-group">
                    <label className="form-label">{t('settings.business.name', 'Business Name')}</label>
                    <input type="text" className="form-input" name="name" value={businessInfo.name} onChange={handleBizChange} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('settings.business.address', 'Business Address')}</label>
                    <textarea className="form-input" name="address" rows="3" value={businessInfo.address} onChange={handleBizChange} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('settings.business.taxId', 'Tax ID / VAT Number')}</label>
                    <input type="text" className="form-input" name="taxId" value={businessInfo.taxId} onChange={handleBizChange} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('settings.business.gstin', 'GSTIN')}</label>
                    <input type="text" className="form-input" name="gstin" value={businessInfo.gstin} onChange={handleBizChange} placeholder="e.g. 27AAPFU0939F1ZV" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('settings.business.state', 'Business State (Place of Origin for GST)')}</label>
                    <select
                      className="form-input"
                      name="state"
                      value={businessInfo.state || ''}
                      onChange={handleBizChange}
                    >
                      <option value="">-- Select State / Union Territory --</option>
                      {INDIAN_STATES.map((s) => (
                        <option key={s.code} value={s.name}>
                          {s.code} - {s.name}
                        </option>
                      ))}
                    </select>
                    {businessInfo.gstin && businessInfo.gstin.trim().length >= 2 && (
                      (() => {
                        const match = getStateFromGSTIN(businessInfo.gstin);
                        if (!match) return null;
                        const isMatch = businessInfo.state && businessInfo.state === match.name;
                        return (
                          <div style={{ fontSize: '0.8rem', marginTop: '4px', color: isMatch ? '#2e7d32' : '#ed6c02', fontWeight: 500 }}>
                            {isMatch 
                              ? `✓ State verified from GSTIN code (${match.code} - ${match.name})`
                              : `⚠️ State differs from GSTIN code (${match.code} - ${match.name})`}
                          </div>
                        );
                      })()
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('settings.business.email', 'Email')}</label>
                    <input type="email" className="form-input" name="email" value={businessInfo.email} onChange={handleBizChange} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('settings.business.mobile', 'Mobile')}</label>
                    <input type="tel" className="form-input" name="mobile" value={businessInfo.mobile} onChange={handleBizChange} />
                  </div>
                  <button className="btn btn-primary" type="submit" disabled={bizSaving}>{bizSaving ? t('settings.saving', 'Saving...') : t('settings.business.saveInfo', 'Save Business Info')}</button>
                </form>
              </div>
            )}

            {activeTab === 'bank' && (
              <div className="settings-section">
                <div className="settings-header">
                  <FiCreditCard />
                  <h2>{t('settings.bank.title', 'Bank Details')}</h2>
                </div>
                <form onSubmit={handleBizSave}>
                  <div className="form-group">
                    <label className="form-label">{t('settings.bank.holderName', 'Account Holder Name')}</label>
                    <input type="text" className="form-input" name="bankHolderName" value={businessInfo.bankHolderName} onChange={handleBizChange} placeholder="Enter account holder name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('settings.bank.bankName', 'Bank Name')}</label>
                    <input type="text" className="form-input" name="bankName" value={businessInfo.bankName} onChange={handleBizChange} placeholder="Enter bank name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('settings.bank.accountNumber', 'Account Number')}</label>
                    <input type="text" className="form-input" name="bankAccountNumber" value={businessInfo.bankAccountNumber} onChange={handleBizChange} placeholder="Enter account number" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('settings.bank.ifscCode', 'IFSC Code')}</label>
                    <input type="text" className="form-input" name="bankIfscCode" value={businessInfo.bankIfscCode} onChange={handleBizChange} placeholder="Enter IFSC code" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('settings.bank.branch', 'Branch')}</label>
                    <input type="text" className="form-input" name="bankBranch" value={businessInfo.bankBranch} onChange={handleBizChange} placeholder="Enter branch name" />
                  </div>
                  <button className="btn btn-primary" type="submit" disabled={bizSaving}>{bizSaving ? t('settings.saving', 'Saving...') : t('settings.bank.saveDetails', 'Save Bank Details')}</button>
                </form>
              </div>
            )}

            {activeTab === 'security' && (
              <>
                <div className="settings-section">
                  <div className="settings-header">
                    <FiBell />
                    <h2>{t('settings.security.notifications', 'Notification Settings')}</h2>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('settings.security.emailNotifications', 'Email Notifications')}</label>
                    <div className="checkbox-group">
                      <label className="checkbox-label">
                        <input type="checkbox" name="newInvoiceNotification" checked={notifications.newInvoiceNotification} onChange={handleNotificationChange} />
                        {t('settings.security.newInvoice', 'New Invoice')}
                      </label>
                      <label className="checkbox-label">
                        <input type="checkbox" name="paymentReceivedNotification" checked={notifications.paymentReceivedNotification} onChange={handleNotificationChange} />
                        {t('settings.security.paymentReceived', 'Payment Received')}
                      </label>
                      <label className="checkbox-label">
                        <input type="checkbox" name="paymentOverdueNotification" checked={notifications.paymentOverdueNotification} onChange={handleNotificationChange} />
                        {t('settings.security.paymentOverdue', 'Payment Overdue')}
                      </label>
                    </div>
                  </div>
                </div>

                <div className="settings-section" style={{ marginTop: '24px' }}>
                  <div className="settings-header">
                    <FiLock />
                    <h2>{t('settings.security.settings', 'Security Settings')}</h2>
                  </div>
                  <PasswordChangeForm />
                </div>
              </>
            )}

            {activeTab === 'language' && (
              <div className="settings-section">
                <div className="settings-header">
                  <FiGlobe />
                  <h2>{t('settings.language.heading', 'Language Settings')}</h2>
                </div>
                <div className="form-group" style={{ marginBottom: '24px' }}>
                  <p style={{ color: '#64748b', marginBottom: '16px' }}>
                    {t('settings.language.description', 'Select your preferred language for the application interface.')}
                  </p>
                  
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <button 
                      onClick={() => setLangModal({ show: true, targetLang: 'en' })}
                      style={{
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: i18n.language === 'en' ? '2px solid var(--primary-color)' : '1px solid #e2e8f0',
                        background: i18n.language === 'en' ? 'var(--primary-color-light, #f0fdf4)' : '#fff',
                        color: i18n.language === 'en' ? 'var(--primary-color)' : '#475569',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {t('settings.language.english', 'English')}
                    </button>
                    
                    <button 
                      onClick={() => setLangModal({ show: true, targetLang: 'hi' })}
                      style={{
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: i18n.language === 'hi' ? '2px solid var(--primary-color)' : '1px solid #e2e8f0',
                        background: i18n.language === 'hi' ? 'var(--primary-color-light, #f0fdf4)' : '#fff',
                        color: i18n.language === 'hi' ? 'var(--primary-color)' : '#475569',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {t('settings.language.hindi', 'Hindi (हिंदी)')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <CustomToast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />

      {/* Language Change Confirmation Modal */}
      <Modal
        show={langModal.show}
        onClose={() => setLangModal({ show: false, targetLang: null })}
        title="Apply Language Settings?"
        size="small"
      >
        <div style={{ padding: '24px 20px', textAlign: 'center' }}>
          <FiGlobe size={48} color="var(--primary-color)" style={{ marginBottom: '16px' }} />
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1e293b', marginBottom: '8px' }}>
            {t('settings.modal.changeLanguage', 'Change Language')}
          </h3>
          <p style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.5', marginBottom: '24px' }}>
            {t('settings.modal.refreshConfirm', 'The application will refresh to fully apply the language changes. Are you sure you want to proceed?')}
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button 
              className="btn btn-outline" 
              onClick={() => setLangModal({ show: false, targetLang: null })}
              style={{ flex: 1, padding: '10px' }}
            >
              {t('settings.modal.cancel', 'Cancel')}
            </button>
            <button 
              className="btn btn-primary" 
              onClick={() => {
                i18n.changeLanguage(langModal.targetLang).then(() => {
                  window.location.reload();
                });
              }}
              style={{ flex: 1, padding: '10px' }}
            >
              Confirm
            </button>
          </div>
        </div>
      </Modal>
    </main>
  );
};

export default SettingsPage;

// ─── Subscription Countdown Card ────────────────────────────────────────────
const SubscriptionCountdownCard = () => {
  const navigate = useNavigate();
  const { hasTrial, isExpired, daysRemaining, trialEndDate, status, loading, planName, endDate } = useTrialStatus();

  // Live countdown timer (updates every second for the last 24 hours)
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const expiryDate = endDate ? new Date(endDate) : (trialEndDate ? new Date(trialEndDate) : null);
    if (!expiryDate) return;

    const tick = () => {
      const now = new Date();
      const diff = expiryDate.getTime() - now.getTime();
      if (diff <= 0) { setTimeLeft({ hours: 0, minutes: 0, seconds: 0 }); return; }
      const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const m = Math.floor((diff / (1000 * 60)) % 60);
      const s = Math.floor((diff / 1000) % 60);
      setTimeLeft({ hours: h, minutes: m, seconds: s });
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [endDate, trialEndDate]);

  // Always dynamically calculate accurate days remaining locally
  const actualDaysRemaining = React.useMemo(() => {
    const expiry = endDate ? new Date(endDate) : (trialEndDate ? new Date(trialEndDate) : null);
    if (!expiry) return daysRemaining;
    const diff = expiry.getTime() - new Date().getTime();
    if (diff <= 0) return 0;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  }, [endDate, trialEndDate, daysRemaining]);

  if (loading) return null;

  const isPaid = status === 'active';
  const isTrial = hasTrial && !isExpired;
  const expired = isExpired || status === 'expired';

  // Determine display values
  const displayPlanName = planName || (isPaid ? 'Active Plan' : isTrial ? 'Free Trial' : expired ? 'Expired' : 'Free Trial');
  const expiryDateDisplay = endDate || trialEndDate;
  const totalDays = isPaid
    ? (planName === '1 Month' ? 30 : planName === '6 Months' ? 180 : planName === '1 Year' ? 365 : (planName === '10 Years' || planName === '25 Years') ? 3650 : 30)
    : 7;
  const progress = Math.max(0, Math.min(100, (actualDaysRemaining / totalDays) * 100));

  const statusColor = isPaid ? '#198754' : isTrial ? '#0dcaf0' : '#dc3545';
  const statusLabel = isPaid ? 'ACTIVE' : isTrial ? 'TRIAL' : 'EXPIRED';
  const cardBg = isPaid ? 'linear-gradient(135deg, #0f2027, #203a43, #2c5364)'
    : isTrial ? 'linear-gradient(135deg, #1a1a2e, #16213e, #0f3460)'
      : 'linear-gradient(135deg, #4a0e0e, #7b1c1c, #a52727)';

  return (
    <div style={{
      gridColumn: '1 / -1', // Full width across all settings columns
      background: cardBg,
      borderRadius: 20,
      padding: '28px 32px',
      color: '#fff',
      marginBottom: 4,
      display: 'flex',
      alignItems: 'center',
      gap: 28,
      flexWrap: 'wrap',
      boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
    }}>
      {/* Progress Circle */}
      <div style={{ position: 'relative', width: 96, height: 96, flexShrink: 0 }}>
        <svg width="96" height="96" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="48" cy="48" r="40" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="8" />
          <circle
            cx="48" cy="48" r="40" fill="none"
            stroke={statusColor} strokeWidth="8"
            strokeDasharray={`${2 * Math.PI * 40}`}
            strokeDashoffset={`${2 * Math.PI * 40 * (1 - progress / 100)}`}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.8s ease' }}
          />
        </svg>
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', textAlign: 'center',
        }}>
          <span style={{ fontSize: 20, fontWeight: 800, lineHeight: 1 }}>
            {expired ? '0' : actualDaysRemaining}
          </span>
          <span style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>
            {expired ? 'EXPIRED' : 'DAYS LEFT'}
          </span>
        </div>
      </div>

      {/* Main Info */}
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <FiAward size={18} />
          <span style={{ fontWeight: 700, fontSize: 18 }}>{displayPlanName}</span>
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
            background: statusColor + '33', color: statusColor, border: `1px solid ${statusColor}55`,
            letterSpacing: 1,
          }}>
            {statusLabel}
          </span>
        </div>

        {expiryDateDisplay && !expired && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, opacity: 0.75, marginBottom: 8 }}>
            <FiCalendar size={13} />
            Expires: {new Date(expiryDateDisplay).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        )}

        {/* Live HH:MM:SS countdown (only for last day) */}
        {actualDaysRemaining <= 1 && !expired && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#ffc107', marginBottom: 8 }}>
            <FiClock size={13} />
            {String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')} remaining
          </div>
        )}

        {/* Progress bar */}
        <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 8, height: 6, overflow: 'hidden', maxWidth: 280 }}>
          <div style={{
            width: `${progress}%`, height: '100%',
            background: `linear-gradient(90deg, ${statusColor}, ${statusColor}aa)`,
            borderRadius: 8, transition: 'width 0.8s ease',
          }} />
        </div>
      </div>

      {/* Upgrade button */}
      {!isPaid && (
        <button
          onClick={() => navigate('/subscription')}
          style={{
            background: 'linear-gradient(135deg, #d63384, #b02a5b)',
            border: 'none', color: '#fff', padding: '10px 20px', borderRadius: 10,
            fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(214,51,132,0.4)',
          }}
        >
          ⚡ Upgrade Now
        </button>
      )}
    </div>
  );
};

// Inline Password Change Form component
const PasswordChangeForm = () => {
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [show, setShow] = React.useState({ current: false, next: false, confirm: false });
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');

  const toggle = (key) => setShow((prev) => ({ ...prev, [key]: !prev[key] }));

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Password must contain at least one number';
    }
    if (!/(?=.*[@$!%*?&])/.test(password)) {
      return 'Password must contain at least one special character (@$!%*?&)';
    }
    return null;
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill all password fields.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New password and confirm password do not match.');
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setSubmitting(true);
    try {
      // Use direct fetch instead of authFetch to avoid automatic logout
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      console.log('Sending password change request:', {
        currentPasswordLength: currentPassword?.length,
        newPasswordLength: newPassword?.length,
        tokenExists: !!token
      });

      const res = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      if (res.ok) {
        const responseData = await res.json();

        // Update the token if provided
        if (responseData.access_token) {
          localStorage.setItem('token', responseData.access_token);
        }

        setSuccess('Password updated successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShow({ current: false, next: false, confirm: false });
      } else {
        const errorData = await res.json();
        console.log('Password change error response:', errorData);
        setError(errorData.message || 'Failed to update password');
      }
    } catch (e) {
      console.error('Password change error:', e);
      setError('Failed to update password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Current Password */}
      <div className="form-group">
        <label className="form-label">Current Password</label>
        <div style={{ position: 'relative' }}>
          <input
            type={show.current ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            style={{
              width: '100%',
              padding: '12px 40px 12px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '16px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
          <button
            type="button"
            onClick={() => toggle('current')}
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            {show.current ? '🙈' : '👁️'}
          </button>
        </div>
      </div>

      {/* New Password */}
      <div className="form-group">
        <label className="form-label">New Password</label>
        <div style={{ position: 'relative' }}>
          <input
            type={show.next ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            style={{
              width: '100%',
              padding: '12px 40px 12px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '16px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
          <button
            type="button"
            onClick={() => toggle('next')}
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            {show.next ? '🙈' : '👁️'}
          </button>
        </div>
      </div>

      {/* Confirm New Password */}
      <div className="form-group">
        <label className="form-label">Confirm New Password</label>
        <div style={{ position: 'relative' }}>
          <input
            type={show.confirm ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            style={{
              width: '100%',
              padding: '12px 40px 12px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '16px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
          <button
            type="button"
            onClick={() => toggle('confirm')}
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            {show.confirm ? '🙈' : '👁️'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ color: 'red', marginTop: '8px', marginBottom: '8px', fontSize: '14px' }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ color: 'green', marginTop: '8px', marginBottom: '8px', fontSize: '14px' }}>
          {success}
        </div>
      )}

      <button
        className="btn btn-primary"
        onClick={handleSubmit}
        disabled={submitting}
        style={{ marginTop: '8px' }}
      >
        {submitting ? 'Updating...' : 'Update Password'}
      </button>
    </div>
  );
};